/**
 * What a walkthrough needs to know about THIS visitor's fleet before it can
 * type anything (spec 33 section 5.5).
 *
 * Every read here is one the pages already make (customers, locations,
 * devices, the live map, the board), with the visitor's own session. Nothing
 * is written. A read that fails leaves its tokens unknown, and the steps that
 * need them drop out of the walkthrough (`requires`), so a slow or broken
 * endpoint costs one step, never the whole walkthrough.
 */
import {
  addressNearby,
  firstUnused,
  friendlyOf,
  localDateTime,
  localDateTimeOn,
  makeDriverPhone,
  makeInviteEmail,
  makePlate,
  makePo,
  makeUnit,
  makeVin,
  newCustomerName,
  queryFor,
  SAMPLE_DRIVERS,
  SAMPLE_LOCATIONS,
} from './context';

/** Every token a resolver below can provide, for the contract test. */
export const PROVIDED = Object.freeze([
  'customer',
  'customerQuery',
  'pickup',
  'pickupQuery',
  'drop',
  'dropQuery',
  'driver',
  'truck',
  'po',
  'loadId',
  // `movingPo`: a load on the road this minute, not the visitor's own
  // (findMoving below), for Track it live.
  'movingPo',
  'freightLine',
  // The driver on the load the walkthrough follows (Message the driver),
  // by name, as the driver pickers list them.
  'loadDriver',
  // Spec 53, the optional walkthroughs on a practice fleet's stages: the
  // miles the settlement-due stage's blocked pay suggests (Settle a
  // driver's pay); the held truck, its defect and the work order the
  // driver's report opened (A driver's defect to a repair); the sample rate
  // confirmation's import and its broker (A load from a rate confirmation).
  'payMiles',
  'defectUnit',
  'defectTruckId',
  'defectId',
  'workOrderId',
  'importId',
  'broker',
  // `times`: when a new load's stops are, each as the box's value and as a
  // person reads it (pickupAt / pickupWhen, dropAt / dropWhen).
  'times',
  'pickupAt',
  'pickupWhen',
  'dropAt',
  'dropWhen',
  // Spec 53, the Your company walkthroughs: the sample records they add
  // (minted on every run, so a retry never collides with the last one)...
  'newCustomer',
  'newCustomerEmail',
  'inviteEmail',
  'newUnit',
  'newVin',
  'newPlate',
  // ...and the truck the practice fleet staged for Retire a truck.
  'retireUnit',
  'retireTruckId',
  // Spec 53, Add a location and Add a driver: the place and the person they
  // add, new on every run, and an address near the hub the map can place.
  'newLocation',
  'newLocationAddress',
  'newDriver',
  'newDriverPhone',
]);

/** The address a sample customer's invoices go to: example.com delivers nowhere. */
const SAMPLE_BILLING_EMAIL = 'ap@example.com';

/** A unit number nobody in `trucks` has, tried a few times, or the last one tried. */
function freeUnit(trucks, random = Math.random) {
  const taken = new Set(trucks.map(t => t && t.unit_number));
  let unit = makeUnit(random);
  for (let i = 0; i < 5 && taken.has(unit); i += 1) unit = makeUnit(random);
  return unit;
}

const EARTH_KM = 6371;

/**
 * When a new load's pickup and delivery are. In a practice fleet, tomorrow
 * at 8:00 AM and 2:00 PM: the customer's request the fleet stages
 * (PracticeFleet.requestFor) and spec 53 10.1 say so, and Can we take it
 * asks about tomorrow (training plan F8). A demo visitor, an hour and five
 * hours from now, so the load they make is one the map can follow today.
 */
export function stopTimes({ practice = false } = {}, now = new Date()) {
  const pickupAt = practice ? localDateTimeOn(1, '08:00', now) : localDateTime(60, now);
  const dropAt = practice ? localDateTimeOn(1, '14:00', now) : localDateTime(300, now);
  return {
    pickupAt,
    pickupWhen: friendlyOf(pickupAt, now),
    dropAt,
    dropWhen: friendlyOf(dropAt, now),
  };
}

/** Great-circle distance in km. Plenty for "which yard is nearest". */
export function distanceKm(a, b) {
  const rad = d => (Number(d) * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

const point = (lat, lon) =>
  lat === null || lat === undefined || lon === null || lon === undefined
    ? null
    : Number.isFinite(Number(lat)) && Number.isFinite(Number(lon))
      ? { lat: Number(lat), lon: Number(lon) }
      : null;

/**
 * The yard: a device bonded to a driver AND a truck, reporting (not stale),
 * whose driver and truck are on no undelivered load. That is the one pair a
 * new load can be given and become the only candidate for the device, so the
 * real ingest attributes its positions to the new load (spec 33 4.5).
 */
export function findYard({ devices = [], live = [], loads = [] }) {
  const busyDrivers = new Set();
  const busyTrucks = new Set();
  for (const load of loads) {
    const driverId = load.driver_id || (load.driver && load.driver.id);
    const truckId = load.truck_id || (load.truck && load.truck.id);
    if (driverId) busyDrivers.add(Number(driverId));
    if (truckId) busyTrucks.add(Number(truckId));
  }
  const fixes = new Map(live.map(d => [Number(d.id), d]));
  for (const device of devices) {
    if (!device.driver_id || !device.truck_id || device.status === 'revoked') continue;
    if (busyDrivers.has(Number(device.driver_id)) || busyTrucks.has(Number(device.truck_id))) {
      continue;
    }
    const fix = fixes.get(Number(device.id));
    const at = fix && point(fix.latitude, fix.longitude);
    if (!at || fix.stale) continue;
    return {
      driver: device.driver_name || null,
      truck: device.truck_unit_number || null,
      at,
    };
  }
  return null;
}

/**
 * A load on the road: in transit, placed by a device fix that is not stale,
 * and not `except` (the visitor's own PO). The newest fix wins, so it is the
 * truck most likely moving as the visitor looks. `live` is GET
 * /tracking/live's `loads` (custody per load id, no PO), `loads` the board's
 * list, which carries the PO the rail row is keyed by. Found by that
 * property, not by number: a regional fleet's seed numbers its loads its own
 * way. Null when nothing is on the road.
 */
export function findMoving({ live = [], loads = [], except = null }) {
  const poOf = new Map(loads.filter(l => l && l.po).map(l => [Number(l.id), l.po]));
  const moving = live
    .filter(entry => {
      const custody = entry && entry.custody;
      const position = custody && custody.position;
      return (
        Boolean(custody) &&
        custody.state === 'in_transit' &&
        position &&
        position.basis === 'device_fix' &&
        !position.stale
      );
    })
    .map(entry => ({
      po: poOf.get(Number(entry.load_id)),
      at: new Date(entry.custody.position.as_of || 0).getTime() || 0,
      id: Number(entry.load_id),
    }))
    .filter(m => m.po && m.po !== except)
    .sort((a, b) => b.at - a.at || a.id - b.id);
  return moving.length ? moving[0].po : null;
}

/**
 * Pickup and drop for the load the walkthrough creates: the geocoded location
 * nearest the yard truck (so the load starts where its truck is), and the
 * nearest other one at least `minKm` away (so the trip is a trip). With no
 * yard, the first two geocoded locations by name.
 */
export function chooseStops(locations = [], yard = null, minKm = 15) {
  const placed = locations
    .map(l => ({ ...l, at: point(l.latitude, l.longitude) }))
    .filter(l => l.at && l.name)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  if (placed.length < 2) return null;
  if (!yard) return { pickup: placed[0], drop: placed[1] };
  const byDistance = (from, list) =>
    list
      .map(l => ({ l, km: distanceKm(from, l.at) }))
      .sort((a, b) => a.km - b.km)
      .map(x => x.l);
  const pickup = byDistance(yard.at, placed)[0];
  const others = byDistance(
    pickup.at,
    placed.filter(l => l.id !== pickup.id)
  );
  const drop = others.find(l => distanceKm(pickup.at, l.at) >= minKm) || others[0];
  return { pickup, drop };
}

/**
 * The miles to suggest for a load's blocked pay (Settle a driver's pay): the
 * figure its allocation itself suggests for review, the planned miles
 * (PayAllocationService.resolveMiles' `suggested`, kept in the allocation's
 * inputs), whole. `rows` is GET /settlements/exceptions; only the load
 * carrying `po` that waits on miles counts. Null when there is none.
 */
export function suggestedMiles(rows, po) {
  if (!Array.isArray(rows) || !po) return null;
  const row = rows.find(r => r && r.po === po && r.exception_code === 'MISSING_APPROVED_MILES');
  const mileage = row && row.inputs && row.inputs.mileage;
  const miles = mileage && mileage.suggested && Number(mileage.suggested.miles);
  return Number.isFinite(miles) && miles > 0 ? String(Math.round(miles)) : null;
}

/**
 * The defect holding a truck on a work order its report opened (A driver's
 * defect to a repair): of the maintenance board's open defects, the newest
 * flagged as affecting safe operation that is on a unit and a work order,
 * which is the one the practice fleet's defect-reported stage filed. Null
 * when there is none.
 */
export function heldDefect(defects) {
  const held = (Array.isArray(defects) ? defects : [])
    .filter(d => d && d.affects_safe_operation && d.truck_id && d.work_order_id && d.unit_number)
    .sort((a, b) => Number(b.id) - Number(a.id));
  const d = held[0];
  return d
    ? {
        defectUnit: String(d.unit_number),
        defectTruckId: String(d.truck_id),
        defectId: String(d.id),
        workOrderId: String(d.work_order_id),
      }
    : null;
}

/**
 * The rate confirmation waiting in review (A load from a rate confirmation):
 * the newest import in `needs_review`, which on a practice fleet is the
 * sample the rate-con-imported stage just imported, and the broker it names.
 * `rows` is GET /imports/rate-confirmations. Null when there is none.
 */
export function importInReview(rows) {
  const waiting = (Array.isArray(rows) ? rows : [])
    .filter(r => r && r.status === 'needs_review')
    .sort((a, b) => Number(b.id) - Number(a.id));
  const row = waiting[0];
  if (!row) return null;
  const broker = row.summary && row.summary.customer_name;
  return broker
    ? { importId: String(row.id), broker: String(broker) }
    : { importId: String(row.id) };
}

const RETRY_MS = 800;

/** One read, tried twice: a dropped connection should not cost the walkthrough a step. */
async function read(http, url, params, { retryMs = RETRY_MS } = {}) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await http.get(url, params ? { params } : undefined);
      return res && res.data !== undefined ? res.data : null;
    } catch (e) {
      // eslint-disable-next-line no-await-in-loop
      if (attempt === 0) await new Promise(resolve => setTimeout(resolve, retryMs));
    }
  }
  return null;
}

/**
 * Resolve what a walkthrough's `needs` names. `known` is what this tab
 * already remembers (persistence's context); it wins for `po` and `loadId`
 * only when the walkthrough is not the one that makes them.
 *
 * `fixed.po` is a practice fleet's own PO (spec 53 5.4, GET /organization's
 * `demo.training.po`), and it wins over both: what the tab remembers and a
 * newly minted one. The fleet's stages continue the newest load carrying that
 * PO and its evidence checks it, so a walkthrough that typed or followed any
 * other would make a load nothing after it can find (training plan F7). It
 * is the ui that follows the server here, not the server that learns the
 * ui's random PO: the stages build loads before any walkthrough runs, and
 * only the server knows the PO then. A demo visitor has no `fixed`.
 *
 * @param {object} http the app's HTTP client
 * @param {string[]} needs
 * @param {object} [known]
 * @param {object} [options] { fresh: ['po'] } values to mint anew,
 *   { fixed: { po } } the practice fleet's own, { practice } in a practice fleet
 */
export async function resolveContext(
  http,
  needs = [],
  known = {},
  { fresh = [], fixed = {}, practice = false, now = new Date() } = {}
) {
  const want = new Set(needs);
  const out = {};
  if (want.has('times')) Object.assign(out, stopTimes({ practice }, now));
  for (const key of ['po', 'loadId']) {
    if (want.has(key) && !fresh.includes(key) && known[key] !== undefined) out[key] = known[key];
  }
  if (want.has('po') && fresh.includes('po')) out.po = makePo();
  if (want.has('po') && fixed.po) {
    // A remembered load id belongs to the remembered PO: with another PO it
    // is somebody else's load, and the host looks the right one up.
    if (out.po !== fixed.po) delete out.loadId;
    out.po = fixed.po;
  }

  if (want.has('customer')) {
    const customers = await read(http, '/customers');
    const first = Array.isArray(customers) ? customers.find(c => c && c.name) : null;
    if (first) {
      out.customer = first.name;
      out.customerQuery = queryFor(first.name);
    }
  }

  // The optional walkthroughs' stages, each read where its page reads it.
  if (want.has('payMiles')) {
    const miles = suggestedMiles(await read(http, '/settlements/exceptions'), out.po);
    if (miles) out.payMiles = miles;
  }
  if (['defectUnit', 'defectTruckId', 'defectId', 'workOrderId'].some(k => want.has(k))) {
    const board = await read(http, '/fleet/maintenance/board');
    Object.assign(out, heldDefect(board && board.open_defects) || {});
  }
  if (want.has('importId') || want.has('broker')) {
    Object.assign(out, importInReview(await read(http, '/imports/rate-confirmations')) || {});
  }

  const needYard = want.has('driver') || want.has('truck') || want.has('pickup');
  let yard = null;
  if (needYard) {
    const [devices, live, loads] = await Promise.all([
      read(http, '/devices'),
      read(http, '/tracking/live'),
      read(http, '/loads', { delivered: false }),
    ]);
    yard = findYard({
      devices: Array.isArray(devices) ? devices : [],
      live: live && Array.isArray(live.devices) ? live.devices : [],
      loads: Array.isArray(loads) ? loads : [],
    });
    if (yard && want.has('driver') && yard.driver) out.driver = yard.driver;
    if (yard && want.has('truck') && yard.truck) out.truck = yard.truck;
  }

  if (want.has('movingPo')) {
    const [live, loads] = await Promise.all([
      read(http, '/tracking/live'),
      read(http, '/loads', { delivered: false }),
    ]);
    const po = findMoving({
      live: live && Array.isArray(live.loads) ? live.loads : [],
      loads: Array.isArray(loads) ? loads : [],
      except: out.po || null,
    });
    if (po) out.movingPo = po;
  }

  // A new customer's name must be one the tenant has not got, archived
  // customers included (customers are unique by name in a tenant).
  if (want.has('newCustomer')) {
    const [active, archived] = await Promise.all([
      read(http, '/customers'),
      read(http, '/customers', { archived: true }),
    ]);
    if (Array.isArray(active)) {
      const names = [...active, ...(Array.isArray(archived) ? archived : [])].map(c => c && c.name);
      const name = newCustomerName(names);
      if (name) out.newCustomer = name;
    }
  }
  if (want.has('newCustomerEmail')) out.newCustomerEmail = SAMPLE_BILLING_EMAIL;
  if (want.has('inviteEmail')) out.inviteEmail = makeInviteEmail();
  if (want.has('newUnit')) {
    const trucks = await read(http, '/trucks');
    out.newUnit = freeUnit(Array.isArray(trucks) ? trucks : []);
  }
  if (want.has('newVin')) out.newVin = makeVin();
  if (want.has('newPlate')) out.newPlate = makePlate();

  // The practice fleet's staged truck (GET /organization's
  // `demo.training.retire_truck`), read now rather than from the store: the
  // stage ran after the app loaded its organization.
  if (want.has('retireUnit') || want.has('retireTruckId')) {
    const org = await read(http, '/organization');
    const training = org && org.demo && org.demo.training;
    const truck = training && training.retire_truck;
    if (truck && truck.id && truck.unit_number) {
      out.retireUnit = truck.unit_number;
      out.retireTruckId = truck.id;
    }
  }

  if (want.has('pickup') || want.has('drop')) {
    const locations = await read(http, '/locations');
    const stops = chooseStops(Array.isArray(locations) ? locations : [], yard);
    if (stops) {
      out.pickup = stops.pickup.name;
      out.pickupQuery = queryFor(stops.pickup.name);
      out.drop = stops.drop.name;
      out.dropQuery = queryFor(stops.drop.name);
    }
  }

  // A new place's name is one the tenant has not used, archived places
  // included; its address is one the map already placed for this tenant.
  if (want.has('newLocation') || want.has('newLocationAddress')) {
    const [active, archived] = await Promise.all([
      read(http, '/locations'),
      want.has('newLocation') ? read(http, '/locations', { archived: true }) : null,
    ]);
    const places = Array.isArray(active) ? active : [];
    if (want.has('newLocation') && Array.isArray(active)) {
      const names = [...places, ...(Array.isArray(archived) ? archived : [])].map(l => l && l.name);
      const name = firstUnused(SAMPLE_LOCATIONS, names);
      if (name) out.newLocation = name;
    }
    if (want.has('newLocationAddress')) {
      const address = addressNearby(places);
      if (address) out.newLocationAddress = address;
    }
  }

  // A new driver's name must be one the tenant has not got, archived drivers
  // included (a driver's name is unique in a tenant).
  if (want.has('newDriver')) {
    const [active, archived] = await Promise.all([
      read(http, '/drivers'),
      read(http, '/drivers', { archived: true }),
    ]);
    if (Array.isArray(active)) {
      const names = [...active, ...(Array.isArray(archived) ? archived : [])].map(d => d && d.name);
      const name = firstUnused(SAMPLE_DRIVERS, names);
      if (name) out.newDriver = name;
    }
  }
  if (want.has('newDriverPhone')) out.newDriverPhone = makeDriverPhone();
  return out;
}

/**
 * The invoice line a load's freight goes on: CreateInvoice offers VAN FREIGHT
 * and TANK FREIGHT and picks between them by the load's own type, so the
 * walkthrough does the same.
 */
export async function freightLineFor(http, loadId) {
  if (!loadId) return null;
  const load = await read(http, `/loads/${loadId}`);
  if (!load) return null;
  return load.type === 'tank' ? 'TANK FREIGHT' : 'VAN FREIGHT';
}

/**
 * The name of the driver on a load, as GET /drivers and the pickers name
 * them, or null when it has none (or the read fails).
 */
export async function driverNameFor(http, loadId) {
  if (!loadId) return null;
  const load = await read(http, `/loads/${loadId}`);
  const driver = load && load.driver;
  return driver && driver.name ? driver.name : null;
}

/**
 * A load's id from its PO, in this tenant: the board's own list, undelivered
 * first, then delivered. Null when it is not there (deleted, or a tenant that
 * was reclaimed and replaced since the PO was remembered).
 *
 * `newest: true` is the practice fleet's rule instead: every load the fleet
 * builds for the trainee carries the same PO (a stage starts a fresh one when
 * the last is past it), and "your load" is the newest of them, delivered or
 * not, as the stages (PracticeFleet `newestOwnLoad`) and the evidence
 * (evidence.js `ownLoad`) both take it.
 */
export async function loadIdForPo(http, po, { newest = false } = {}) {
  if (!po) return null;
  let best = null;
  for (const delivered of [false, true]) {
    // eslint-disable-next-line no-await-in-loop
    const loads = await read(http, '/loads', { delivered });
    const hits = Array.isArray(loads) ? loads.filter(l => l && l.po === po) : [];
    if (!newest && hits.length) return hits[0].id;
    for (const hit of hits) if (!best || Number(hit.id) > Number(best.id)) best = hit;
  }
  return best ? best.id : null;
}
